/**
 * Informational Guide Pages
 * These are static/informational pages with minimal state
 */
import { useNavigate } from "react-router-dom";
import { Home, Shield, CheckCircle, X, Phone, Mail, MapPin, Clock, ArrowRight, Star, Cat } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

// Indoor vs Outdoor Page
export const IndoorOutdoorPage = () => {
  const navigate = useNavigate();

  const outdoorDangers = [
    { icon: "🚗", title: "Road Traffic", description: "One of the leading causes of death for outdoor cats. Even quiet residential streets pose significant risks.", severity: "Critical" },
    { icon: "🧊", title: "Deicer & Antifreeze", description: "Puddles containing road deicer or antifreeze are extremely toxic to cats.", severity: "Critical" },
    { icon: "🦅", title: "Birds of Prey", description: "Hawks, buzzards, and owls can attack small cats, especially kittens.", severity: "High" },
    { icon: "🦊", title: "Foxes", description: "While foxes typically avoid adult cats, they can be a danger to kittens and elderly cats.", severity: "Medium" },
    { icon: "🐱", title: "Other Cats", description: "Territorial disputes can result in serious injuries and disease transmission.", severity: "High" },
    { icon: "🐕", title: "Dogs & Other Animals", description: "Encounters with dogs can be fatal. Badgers pose threats in rural areas.", severity: "High" },
    { icon: "🏠", title: "Getting Lost or Trapped", description: "Cats can become trapped in sheds, garages, or construction sites.", severity: "Medium" },
    { icon: "☠️", title: "Poisons & Pesticides", description: "Rodent poisons, slug pellets, and garden chemicals can be fatal.", severity: "High" },
    { icon: "🦠", title: "Parasites & Disease", description: "Outdoor cats are exposed to fleas, ticks, worms, and infectious diseases.", severity: "Medium" },
    { icon: "😈", title: "Human Cruelty", description: "Sadly, some people intentionally harm cats.", severity: "Variable" }
  ];

  const stressFactors = [
    "Constant territorial vigilance and defence",
    "Unpredictable encounters with other animals",
    "Exposure to loud noises (traffic, fireworks, construction)",
    "Weather extremes (heat, cold, rain)",
    "Finding safe shelter and food sources"
  ];

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="indoor-outdoor-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <Badge className="bg-blue-100 text-blue-700 mb-4">Cat Care Guide</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Indoor vs <span className="text-blue-700">Outdoor</span> Cats</h1>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">One of the most important decisions you'll make as a cat owner.</p>
        </div>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 mb-12">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Home className="w-10 h-10 text-green-600" />
              </div>
              <div className="flex-1">
                <Badge className="bg-green-600 text-white mb-3">Our Position</Badge>
                <h2 className="font-bold text-2xl text-stone-900 mb-3">CatPals+ Advocates for Indoor Cats</h2>
                <p className="text-stone-700 mb-4">Based on our many years of experience, we believe <strong>indoor cats live longer, healthier, and less stressful lives</strong>.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/70 rounded-lg p-4">
                    <p className="font-semibold text-green-700 mb-2">Average Lifespan</p>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-green-600">12-18</p>
                        <p className="text-sm text-stone-600">Indoor (years)</p>
                      </div>
                      <span className="text-2xl text-stone-400">vs</span>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-red-600">2-5</p>
                        <p className="text-sm text-stone-600">Outdoor (years)</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/70 rounded-lg p-4">
                    <p className="font-semibold text-green-700 mb-2">Key Benefits</p>
                    <ul className="text-sm text-stone-700 space-y-1">
                      <li>• Protected from traffic and predators</li>
                      <li>• No exposure to toxins or poisons</li>
                      <li>• Reduced stress from territorial disputes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="mb-12">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">Outdoor Dangers</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {outdoorDangers.map((danger, i) => (
              <Card key={i} className={`${danger.severity === 'Critical' ? 'border-red-300 bg-red-50' : danger.severity === 'High' ? 'border-orange-200' : 'border-stone-200'}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{danger.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-stone-900">{danger.title}</h4>
                        <Badge className={`text-xs ${danger.severity === 'Critical' ? 'bg-red-600 text-white' : danger.severity === 'High' ? 'bg-orange-500 text-white' : 'bg-amber-100 text-amber-700'}`}>{danger.severity}</Badge>
                      </div>
                      <p className="text-sm text-stone-600">{danger.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <Card className="bg-blue-50 border-blue-200 mb-12">
          <CardContent className="p-6">
            <h3 className="font-bold text-xl text-blue-800 mb-4">💡 Safe Alternatives</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/70 rounded-lg p-4">
                <h4 className="font-semibold text-blue-700 mb-2">Catios</h4>
                <p className="text-sm text-stone-600">Enclosed outdoor spaces allow fresh air safely.</p>
              </div>
              <div className="bg-white/70 rounded-lg p-4">
                <h4 className="font-semibold text-blue-700 mb-2">Cat-Proof Gardens</h4>
                <p className="text-sm text-stone-600">Special fencing prevents escapes.</p>
              </div>
              <div className="bg-white/70 rounded-lg p-4">
                <h4 className="font-semibold text-blue-700 mb-2">Harness Training</h4>
                <p className="text-sm text-stone-600">Supervised outdoor walks on a leash.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-600 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Need to Microchip Your Cat?</h2>
            <p className="text-amber-100 mb-6">CatPals+ offers professional microchipping at just £10 suggested donation.</p>
            <Button onClick={() => navigate('/microchipping')} className="bg-white text-amber-600 hover:bg-amber-50 rounded-full px-8 py-6 text-lg">
              <Shield className="w-5 h-5 mr-2" /> View Microchipping Services
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Microchipping Page
export const MicrochippingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="microchipping-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <Badge className="bg-purple-100 text-purple-700 mb-4">Pet Safety Service</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Professional <span className="text-purple-700">Microchipping</span></h1>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">Keep your pets safe and legal with our affordable microchipping service.</p>
        </div>

        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-300 mb-8">
          <CardContent className="p-8">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <div className="flex-1">
                <Badge className="bg-purple-600 text-white mb-3">UK Law - June 2024</Badge>
                <h2 className="font-bold text-2xl text-stone-900 mb-4">Cat Microchipping Now Compulsory</h2>
                <p className="text-stone-700 mb-4">As of 10th June 2024, all cats in England must be microchipped by 20 weeks of age. Failure to comply can result in a <strong>fine of up to £500</strong>.</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white/70 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-700 mb-2">Requirements:</h4>
                    <ul className="text-sm text-stone-700 space-y-1">
                      <li>• Microchipped by 20 weeks old</li>
                      <li>• Registered on compliant database</li>
                      <li>• Contact details kept up to date</li>
                    </ul>
                  </div>
                  <div className="bg-white/70 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-700 mb-2">Our Service:</h4>
                    <ul className="text-sm text-stone-700 space-y-1">
                      <li>• Suggested donation: £10</li>
                      <li>• Fully qualified implanter</li>
                      <li>• Quick and virtually painless</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6 text-center">
              <span className="text-4xl mb-4 block">💉</span>
              <h3 className="font-bold text-lg mb-2">Quick Procedure</h3>
              <p className="text-sm text-stone-600">Takes just seconds, similar to a vaccination injection.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <span className="text-4xl mb-4 block">🔒</span>
              <h3 className="font-bold text-lg mb-2">Permanent ID</h3>
              <p className="text-sm text-stone-600">Lasts your pet's lifetime, can't fall off like collars.</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <span className="text-4xl mb-4 block">🏠</span>
              <h3 className="font-bold text-lg mb-2">Reunite Lost Pets</h3>
              <p className="text-sm text-stone-600">Dramatically increases chances of being reunited.</p>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-amber-600 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Book Your Microchipping</h2>
            <p className="text-amber-100 mb-6">Contact us to arrange microchipping for your cat or small mammal.</p>
            <Button onClick={() => navigate('/contact')} className="bg-white text-amber-600 hover:bg-amber-50 rounded-full px-8 py-6 text-lg">
              <Phone className="w-5 h-5 mr-2" /> Contact Us
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Scan Angels Page
export const ScanAngelsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="scan-angels-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <Badge className="bg-pink-100 text-pink-700 mb-4">Community Initiative</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4"><span className="text-pink-700">Scan Angels</span> Network</h1>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">Volunteer network helping reunite lost pets with their families through microchip scanning.</p>
        </div>

        {/* Coverage Area Card */}
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 mb-8">
          <CardContent className="p-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MapPin className="w-5 h-5 text-amber-600" />
              <h3 className="font-bold text-lg text-stone-900">Our Coverage Area</h3>
            </div>
            <p className="text-stone-700">We cover <span className="font-semibold text-amber-700">Norwich and surrounding areas up to 15 miles from the city centre</span>. If you've found a cat within this area, we can help!</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-pink-50 to-rose-50 border-pink-300 mb-8">
          <CardContent className="p-8">
            <h2 className="font-bold text-2xl text-stone-900 mb-4">What are Scan Angels?</h2>
            <p className="text-stone-700 mb-4">Scan Angels are volunteers equipped with microchip scanners who help identify found pets. If you find a stray cat, a Scan Angel can scan for a microchip to help reunite them with their owner.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/70 rounded-lg p-4">
                <h4 className="font-semibold text-pink-700 mb-2">Found a Cat?</h4>
                <ul className="text-sm text-stone-700 space-y-1">
                  <li>• Contact a local Scan Angel</li>
                  <li>• They'll scan for a microchip</li>
                  <li>• Help trace the owner</li>
                </ul>
              </div>
              <div className="bg-white/70 rounded-lg p-4">
                <h4 className="font-semibold text-pink-700 mb-2">Become a Scan Angel</h4>
                <ul className="text-sm text-stone-700 space-y-1">
                  <li>• Help your community</li>
                  <li>• Training provided</li>
                  <li>• Scanner loan available</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-pink-600 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">CatPals+ is Part of the Network</h2>
            <p className="text-pink-100 mb-6">Contact us if you've found a cat and need help scanning for a microchip.</p>
            <Button onClick={() => navigate('/contact')} className="bg-white text-pink-600 hover:bg-pink-50 rounded-full px-8 py-6 text-lg">
              <Phone className="w-5 h-5 mr-2" /> Contact Us
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Food Guide Page
export const FoodGuidePage = () => {
  const premiumBrands = [
    {
      name: "KatKin",
      type: "Premium Fresh",
      rating: 5,
      description: "Fresh, human-grade cat food delivered to your door. Made with 100% real meat and fish, gently cooked to preserve nutrients.",
      pros: ["100% real meat/fish", "No fillers or additives", "Human-grade ingredients", "Tailored meal plans", "Vet-formulated recipes"],
      cons: ["Premium price point", "Requires refrigeration", "Subscription based"],
      protein: "High (60-70%)",
      ingredients: "Whole meat, organs, essential vitamins"
    },
    {
      name: "Marrow (Marro)",
      type: "Premium Fresh",
      rating: 5,
      description: "Premium raw and gently cooked cat food using ethically sourced British ingredients. Minimal processing to retain maximum nutrition.",
      pros: ["Whole meat recipes", "Ethically sourced", "Minimal processing", "No artificial preservatives", "High meat content"],
      cons: ["Higher cost", "Limited availability", "Shorter shelf life"],
      protein: "High (65-75%)",
      ingredients: "British meat, fish, essential nutrients"
    },
    {
      name: "Untamed",
      type: "Premium Wet",
      rating: 5,
      description: "Grain-free wet food with up to 2x more protein than standard brands. Made with human-grade whole meat and fish.",
      pros: ["2x more protein", "Grain-free", "Human-grade meat", "No sugar or grains", "Eco-friendly packaging"],
      cons: ["Premium pricing", "Only wet food available", "Subscription model"],
      protein: "Very High (60-80%)",
      ingredients: "Whole meat/fish, taurine, vitamins"
    }
  ];

  const mainstreamBrands = [
    {
      name: "Felix As Good As It Looks",
      type: "Mainstream Wet",
      rating: 3,
      description: "Popular mid-range wet food with visible meat pieces in jelly or gravy. Widely available and reasonably priced.",
      pros: ["Affordable", "Widely available", "Cats love the taste", "Good variety of flavours", "Contains essential nutrients"],
      cons: ["Contains some fillers", "Added sugars in some varieties", "Lower meat content than premium"],
      protein: "Medium (30-40%)",
      ingredients: "Meat and animal derivatives, cereals, minerals"
    },
    {
      name: "Gourmet",
      type: "Mainstream Wet",
      rating: 3,
      description: "Mid-to-upper range wet food marketed as a gourmet option. Offers various textures from pâté to chunks in sauce.",
      pros: ["Good variety", "Reasonable quality", "Multiple textures available", "Added vitamins", "Palatable for fussy eaters"],
      cons: ["Contains derivatives", "Some fillers present", "Variable meat content"],
      protein: "Medium (35-45%)",
      ingredients: "Meat and animal derivatives, fish, minerals, sugars"
    },
    {
      name: "Sheba",
      type: "Mainstream Wet",
      rating: 3,
      description: "Premium-positioned mainstream brand with single-serve trays and pouches. Known for smoother textures.",
      pros: ["Convenient portions", "Smooth textures", "Good palatability", "No artificial colours", "Decent protein levels"],
      cons: ["Small portion sizes", "Contains some by-products", "Premium mainstream pricing"],
      protein: "Medium (35-40%)",
      ingredients: "Meat and animal derivatives, vegetable protein, minerals"
    },
    {
      name: "Whiskas",
      type: "Mainstream Wet/Dry",
      rating: 3,
      description: "One of the most recognised cat food brands. Offers both wet and dry options at accessible price points.",
      pros: ["Very affordable", "Extremely available", "Wide range of products", "Fortified with vitamins", "Trusted brand"],
      cons: ["Higher filler content", "Contains cereals", "Lower meat percentage"],
      protein: "Medium-Low (28-35%)",
      ingredients: "Meat and animal derivatives, cereals, vegetable protein"
    }
  ];

  const nutritionGuide = [
    { nutrient: "Protein", recommended: "Min 26% (dry), 8% (wet)", importance: "Essential for muscle maintenance and overall health", sources: "Meat, fish, eggs" },
    { nutrient: "Taurine", recommended: "0.1-0.2%", importance: "Critical for heart health, vision, and reproduction - cats cannot produce their own", sources: "Heart, liver, dark meat" },
    { nutrient: "Vitamin B12", recommended: "0.02mg/kg", importance: "Essential for nervous system function and red blood cell formation", sources: "Meat, fish, liver" },
    { nutrient: "Fat", recommended: "9-15%", importance: "Energy source, aids vitamin absorption, healthy skin and coat", sources: "Animal fats, fish oils" },
    { nutrient: "Arachidonic Acid", recommended: "0.02%", importance: "Essential fatty acid for skin, coat, and kidney function", sources: "Animal fats only" },
    { nutrient: "Vitamin A", recommended: "3,333 IU/kg", importance: "Vision, immune function, and skin health - cats cannot convert from plants", sources: "Liver, fish oils" },
    { nutrient: "Calcium", recommended: "0.6-1%", importance: "Bone and teeth health, muscle function", sources: "Bone meal, dairy" },
    { nutrient: "Moisture", recommended: "75-85% (wet)", importance: "Hydration, kidney health, urinary tract function", sources: "Wet food, water" }
  ];

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="food-guide-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="bg-green-100 text-green-700 mb-4">Expert Nutrition Guide</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            Cat Food <span className="text-green-700">Guide</span>
          </h1>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">
            Understanding cat nutrition is essential for your feline's health. Here's our comprehensive guide 
            to cat food types, brands, and what to look for on the label.
          </p>
        </div>

        {/* Premium Brands Section */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">⭐</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-stone-900">Premium Cat Food Brands</h2>
              <p className="text-stone-600">Whole meat/fish with minimal fillers or additives</p>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-6">
            {premiumBrands.map((brand, i) => (
              <Card key={i} className="border-green-200 bg-gradient-to-br from-green-50 to-white">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <Badge className="bg-green-600 text-white">{brand.type}</Badge>
                    <div className="flex">
                      {[...Array(brand.rating)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                  </div>
                  <CardTitle className="text-xl">{brand.name}</CardTitle>
                  <CardDescription>{brand.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-green-700 mb-1">Protein Level: {brand.protein}</p>
                      <p className="text-xs text-stone-500">Key ingredients: {brand.ingredients}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-green-700 mb-1">Pros</p>
                        <ul className="text-xs text-stone-600 space-y-1">
                          {brand.pros.slice(0, 3).map((pro, j) => (
                            <li key={j} className="flex items-start gap-1">
                              <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" /> {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-red-700 mb-1">Cons</p>
                        <ul className="text-xs text-stone-600 space-y-1">
                          {brand.cons.map((con, j) => (
                            <li key={j} className="flex items-start gap-1">
                              <X className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" /> {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Mainstream Brands Section */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🛒</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-stone-900">Mainstream Cat Food Brands</h2>
              <p className="text-stone-600">Widely available mid-range options</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {mainstreamBrands.map((brand, i) => (
              <Card key={i} className="border-amber-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <Badge className="bg-amber-100 text-amber-700">{brand.type}</Badge>
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < brand.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300'}`} />
                      ))}
                    </div>
                  </div>
                  <CardTitle className="text-lg">{brand.name}</CardTitle>
                  <CardDescription className="text-sm">{brand.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <p className="text-sm"><span className="font-medium">Protein:</span> {brand.protein}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-semibold text-green-700 mb-1">Pros</p>
                        <ul className="text-xs text-stone-600 space-y-1">
                          {brand.pros.slice(0, 3).map((pro, j) => (
                            <li key={j}>• {pro}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-red-700 mb-1">Cons</p>
                        <ul className="text-xs text-stone-600 space-y-1">
                          {brand.cons.map((con, j) => (
                            <li key={j}>• {con}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Budget/Low Quality Warning */}
        <Card className="bg-red-50 border-red-200 mb-16">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚠️</span>
              </div>
              <div>
                <h3 className="font-bold text-xl text-red-800 mb-2">Budget & Low-Quality Cat Food Warning</h3>
                <p className="text-stone-700 mb-4">
                  Many mainstream brands also offer cheaper "economy" alternatives, and supermarket own-brands often fall into this category. 
                  These products can be <strong>up to 99% filler</strong> with very little actual meat content.
                </p>
                <div className="bg-white/70 rounded-lg p-4">
                  <p className="font-semibold text-red-700 mb-2">What to watch out for:</p>
                  <ul className="text-sm text-stone-700 space-y-1">
                    <li>• <strong>"Meat and animal derivatives"</strong> as the main ingredient (vague and often low quality)</li>
                    <li>• <strong>Cereals or grains</strong> listed as the first ingredient</li>
                    <li>• <strong>Very low prices</strong> - if it seems too cheap, the quality reflects this</li>
                    <li>• <strong>"With Chicken" or "With Fish"</strong> - legally only needs 4% of that ingredient</li>
                    <li>• <strong>High sugar content</strong> - added to make low-quality food palatable</li>
                  </ul>
                </div>
                <p className="text-red-700 font-medium mt-4">
                  These low-quality foods are really not suitable for any cat's diet and can lead to health issues over time.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wet vs Dry Diet */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">Wet Food vs Dry Food: Which is Best?</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-blue-200">
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">🥫</span> Wet Food Diet
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-green-700 mb-2 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Advantages
                    </p>
                    <ul className="text-sm text-stone-600 space-y-2">
                      <li>• <strong>High moisture content (75-85%)</strong> - excellent for hydration and kidney health</li>
                      <li>• <strong>More palatable</strong> - better for fussy eaters</li>
                      <li>• <strong>Closer to natural diet</strong> - mimics prey moisture levels</li>
                      <li>• <strong>Lower calorie density</strong> - helps prevent obesity</li>
                      <li>• <strong>Easier to eat</strong> - good for senior cats or those with dental issues</li>
                      <li>• <strong>Reduces urinary tract issues</strong> - increased water intake</li>
                    </ul>
                  </div>
                  <Separator />
                  <div>
                    <p className="font-semibold text-red-700 mb-2 flex items-center gap-1">
                      <X className="w-4 h-4" /> Disadvantages
                    </p>
                    <ul className="text-sm text-stone-600 space-y-2">
                      <li>• <strong>Shorter shelf life</strong> - must be refrigerated once opened</li>
                      <li>• <strong>More expensive per serving</strong></li>
                      <li>• <strong>No dental benefits</strong> - doesn't help clean teeth</li>
                      <li>• <strong>Messier</strong> - can stick to bowls and whiskers</li>
                      <li>• <strong>Spoils quickly</strong> - can't be left out all day</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200">
              <CardHeader className="bg-orange-50">
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">🥣</span> Dry Food (Biscuits) Diet
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-green-700 mb-2 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> Advantages
                    </p>
                    <ul className="text-sm text-stone-600 space-y-2">
                      <li>• <strong>Convenient</strong> - can be left out for free-feeding</li>
                      <li>• <strong>Economical</strong> - lower cost per serving</li>
                      <li>• <strong>Long shelf life</strong> - doesn't spoil quickly</li>
                      <li>• <strong>Dental benefits</strong> - some kibble helps reduce tartar</li>
                      <li>• <strong>Less messy</strong> - easier to clean up</li>
                      <li>• <strong>Higher calorie density</strong> - good for underweight cats</li>
                    </ul>
                  </div>
                  <Separator />
                  <div>
                    <p className="font-semibold text-red-700 mb-2 flex items-center gap-1">
                      <X className="w-4 h-4" /> Disadvantages
                    </p>
                    <ul className="text-sm text-stone-600 space-y-2">
                      <li>• <strong>Low moisture (6-10%)</strong> - cats may not drink enough water</li>
                      <li>• <strong>Higher carbohydrate content</strong> - not natural for cats</li>
                      <li>• <strong>Risk of dehydration</strong> - can lead to kidney/urinary issues</li>
                      <li>• <strong>Easy to overfeed</strong> - calorie-dense, watch portions</li>
                      <li>• <strong>Less palatable</strong> - some cats refuse to eat it</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <Card className="mt-6 bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <p className="text-stone-700 text-center">
                <strong>Our recommendation:</strong> A combination of both wet and dry food often provides the best balance - 
                wet food for hydration and nutrition, with some dry food for dental health and convenience.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Breed Specific Foods */}
        <Card className="mb-16 bg-purple-50 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Cat className="w-7 h-7 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-xl text-purple-800 mb-2">Breed-Specific Cat Food</h3>
                <p className="text-stone-700 mb-3">
                  Breed-specific cat foods are formulated to address the unique nutritional needs of particular breeds. 
                  For example, Maine Coons may need joint support, while Persians benefit from kibble shapes designed for their flat faces.
                </p>
                <p className="text-stone-700">
                  <strong>Where to find:</strong> Breed-specific foods are readily available at most major pet retailers 
                  (Pets at Home, Pet Planet, etc.) and are often sold directly at veterinary practices. 
                  Brands like Royal Canin and Hill's Science Diet offer extensive breed-specific ranges.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Health Monitoring */}
        <Card className="mb-16 bg-red-50 border-red-300">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🚨</span>
              </div>
              <div>
                <h3 className="font-bold text-xl text-red-800 mb-2">Important: Monitor Your Cat's Health</h3>
                <p className="text-stone-700 mb-4">
                  <strong>Keep a close eye on the litter box!</strong> Your cat's litter box can tell you a lot about their health. 
                  Changes in frequency, consistency, colour, or smell of waste can indicate dietary issues or health problems.
                </p>
                <div className="bg-white/70 rounded-lg p-4 mb-4">
                  <p className="font-semibold text-red-700 mb-2">Seek immediate veterinary advice if you notice:</p>
                  <ul className="text-sm text-stone-700 space-y-1 grid md:grid-cols-2 gap-2">
                    <li>• Diarrhoea lasting more than 24 hours</li>
                    <li>• Blood in urine or faeces</li>
                    <li>• Straining to urinate (emergency!)</li>
                    <li>• Significant change in frequency</li>
                    <li>• Very dark or tar-like stools</li>
                    <li>• Complete loss of appetite</li>
                  </ul>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="font-semibold text-amber-800 mb-2">Multi-Cat Households with Special Diets</p>
                  <p className="text-stone-700 text-sm">
                    If your cat is prescribed a special veterinary diet, consider investing in a <strong>microchip cat feeder</strong>. 
                    These smart feeders only open for the registered cat's microchip, ensuring each cat in a multi-cat household 
                    only eats the correct food for their specific dietary needs. Brands like SureFeed offer reliable options.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Nutritional Values Guide */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-stone-900 mb-6 text-center">Nutritional Values Guide (UK Market)</h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-stone-100">
                      <TableHead className="font-bold">Nutrient</TableHead>
                      <TableHead className="font-bold">Recommended Level</TableHead>
                      <TableHead className="font-bold">Why It's Important</TableHead>
                      <TableHead className="font-bold">Natural Sources</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nutritionGuide.map((item, i) => (
                      <TableRow key={i} className={item.nutrient === "Taurine" || item.nutrient === "Vitamin B12" ? "bg-amber-50" : ""}>
                        <TableCell className="font-semibold">
                          {item.nutrient}
                          {(item.nutrient === "Taurine" || item.nutrient === "Vitamin B12") && (
                            <Badge className="ml-2 bg-amber-500 text-white text-xs">Essential</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{item.recommended}</TableCell>
                        <TableCell className="text-sm">{item.importance}</TableCell>
                        <TableCell className="text-sm">{item.sources}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          <Card className="mt-4 bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <p className="text-stone-700">
                <strong>Note:</strong> Both <strong>Vitamin B12</strong> and <strong>Taurine</strong> are absolutely essential for cats 
                and cannot be synthesised by their bodies in sufficient quantities. If you're concerned your cat's diet may be lacking, 
                both can be added as supplements to any cat food.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Supplement Recommendation */}
        <Card className="mb-12 bg-gradient-to-br from-green-50 to-emerald-50 border-green-300">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-3xl">💊</span>
              </div>
              <div className="flex-1">
                <Badge className="bg-green-600 text-white mb-2">Our Recommendation</Badge>
                <h3 className="font-bold text-2xl text-stone-900 mb-3">Feline 40 Supplement</h3>
                <p className="text-stone-700 mb-4">
                  We highly recommend a supplement called <strong>Feline 40</strong> for supporting your cat's overall health and nutrition. 
                  It's an excellent way to ensure your cat gets all the essential vitamins and minerals they need, 
                  regardless of what food they're eating.
                </p>
                <div className="bg-white/70 rounded-lg p-4">
                  <p className="text-sm text-stone-600 italic flex items-start gap-2">
                    <span className="text-amber-500">⚠️</span>
                    <span>
                      <strong>Disclaimer:</strong> We are not sponsored or endorsed to recommend any products on this site. 
                      All recommendations come from pure experience and many years of caring for our furry friends!
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-green-700 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Questions About Cat Nutrition?</h2>
            <p className="text-green-100 mb-6 max-w-2xl mx-auto">
              Our team is always happy to help with cat care questions. Get in touch if you need personalised nutrition advice!
            </p>
            <Button 
              onClick={() => window.location.href = '/contact'} 
              className="bg-white text-green-700 hover:bg-green-50 rounded-full px-8"
            >
              <Phone className="w-4 h-4 mr-2" /> Contact Us
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Litter Guide Page
export const LitterGuidePage = () => {
  const litterTypes = [
    {
      name: "Wood Pellet Cat Litter",
      icon: "🌲",
      pros: ["Eco-friendly & biodegradable", "Excellent odour control", "Low dust", "Can be composted", "Good absorbency"],
      cons: ["Can be rough on paws", "Pellets can scatter", "Some cats dislike the texture", "Requires more frequent full changes"],
      bestFor: "Environmentally conscious cat owners who don't mind the texture",
      rating: 4,
      description: "Made from compressed sawdust, wood pellet litter is a sustainable choice. The pellets absorb moisture and break down into sawdust, which can then be sifted out. It's naturally antibacterial and has a pleasant woody scent."
    },
    {
      name: "Cardboard Cat Litter",
      icon: "📦",
      pros: ["Very lightweight", "Biodegradable", "Soft on paws", "Low dust", "Made from recycled materials"],
      cons: ["Less effective odour control", "Not as absorbent", "Can stick to wet fur", "May need more frequent changes"],
      bestFor: "Cats with sensitive paws or owners wanting recycled materials",
      rating: 3,
      description: "An eco-friendly option made from recycled cardboard or paper. It's extremely lightweight and gentle on paws, making it suitable for post-surgery cats or those with sensitivities. However, it may require more frequent changing."
    },
    {
      name: "Non-Clumping Lightweight Cat Litter",
      icon: "🪶",
      pros: ["Very easy to carry", "Soft texture", "Safe for kittens", "Less tracking", "Affordable"],
      cons: ["Doesn't clump - harder to scoop", "Requires full changes more often", "Less effective long-term odour control"],
      bestFor: "Kittens under 6 months, elderly owners, or multi-storey homes",
      rating: 4,
      recommended: "kitten",
      description: "Lightweight litters are typically made from materials like clay mixed with lighter substances. They're much easier to carry and pour than traditional litters. RECOMMENDED FOR KITTENS UP TO 6 MONTHS as they may ingest clumping litter during grooming."
    },
    {
      name: "Clay Clumping Cat Litter (Large Grain)",
      icon: "�ite",
      pros: ["Excellent clumping", "Good odour control", "Easy to scoop", "Long-lasting", "Widely available"],
      cons: ["Heavy to carry", "Can be dusty", "Not biodegradable", "May track outside box"],
      bestFor: "Adult cats and owners wanting traditional, reliable litter",
      rating: 4,
      description: "Traditional bentonite clay litter with larger granules. Forms solid clumps when wet, making daily scooping easy and efficient. The larger grain size reduces tracking but may feel less natural to some cats."
    },
    {
      name: "Clay Clumping Cat Litter (Fine Grain)",
      icon: "�ite",
      pros: ["Superior clumping", "Cats often prefer texture", "Excellent odour control", "Very easy to scoop"],
      cons: ["More dusty than large grain", "Heavier tracking", "Not eco-friendly", "Can stick to litter box"],
      bestFor: "Cats who prefer sand-like texture, single-cat households",
      rating: 4,
      description: "Fine grain clay litter mimics natural sand, which many cats instinctively prefer. It forms very tight clumps but can create more dust and tends to track more easily on paws."
    },
    {
      name: "Tofu Cat Litter",
      icon: "🫘",
      pros: ["Flushable", "Biodegradable", "Low dust", "Lightweight", "Good clumping", "Safe if ingested"],
      cons: ["Can develop mould if damp", "Some cats dislike smell", "More expensive", "May attract insects"],
      bestFor: "Eco-conscious owners wanting flushable litter",
      rating: 4,
      description: "Made from soybean/tofu residue, this innovative litter is completely natural and can be safely flushed (check local regulations). It clumps well and is safe if accidentally ingested by curious cats."
    },
    {
      name: "Corn Clumping Cat Litter (Extra Fine)",
      icon: "🌽",
      pros: ["Almost zero dust", "Excellent clumping - doesn't stick to box", "Natural & biodegradable", "Flushable", "Pleasant natural scent", "Safe if ingested"],
      cons: ["Premium price point", "May attract insects if stored improperly", "Some cats need adjustment period"],
      bestFor: "All cats, especially those with respiratory sensitivities",
      rating: 5,
      recommended: "top",
      description: "Corn-based litters like World's Best Cat Litter offer exceptional performance. The extra-fine texture creates superior clumps that lift cleanly from the box without sticking to the bottom. Virtually dust-free, making it ideal for cats and owners with allergies."
    }
  ];

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="litter-guide-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge className="bg-amber-100 text-amber-700 mb-4">Expert Guide</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            Cat Litter <span className="text-amber-600">Guide</span>
          </h1>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">
            Choosing the right cat litter is important for your cat's health and happiness. 
            Here's our comprehensive guide to help you make the best choice.
          </p>
        </div>

        {/* Important Notice for Kittens */}
        <Card className="bg-amber-50 border-amber-200 mb-12">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🐱</span>
              </div>
              <div>
                <h3 className="font-bold text-xl text-stone-900 mb-2">Important: Kittens Under 6 Months</h3>
                <p className="text-stone-700">
                  <strong>We recommend that kittens up to 6 months of age use lightweight non-clumping cat litter.</strong> Young kittens 
                  are curious and may ingest clumping litter during grooming, which can cause dangerous intestinal blockages. 
                  Non-clumping litter is safer for their developing digestive systems.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Our Top Recommendation */}
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 mb-12">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-4xl">🌽</span>
              </div>
              <div className="flex-1">
                <Badge className="bg-green-600 text-white mb-3">Our Top Recommendation</Badge>
                <h3 className="font-bold text-2xl text-stone-900 mb-3">World's Best Cat Litter</h3>
                <p className="text-stone-700 mb-4">
                  We highly recommend <strong>World's Best Cat Litter</strong>, a premium corn-based litter that stands out 
                  from the competition. Here's why:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-stone-700"><strong>Almost zero dust</strong> - Perfect for cats and owners with respiratory sensitivities</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-stone-700"><strong>Unique clumping ability</strong> - Clumps don't stick to the bottom of the litter box</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-stone-700"><strong>Excellent odour control</strong> - Natural corn absorbs and traps odours</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-stone-700"><strong>Easy litter box cleaning</strong> - Clumps lift out cleanly every time</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-stone-700"><strong>Natural & flushable</strong> - Made from whole-kernel corn</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-stone-700"><strong>Long-lasting</strong> - Excellent value despite premium price</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Litter Types Grid */}
        <h2 className="text-3xl font-bold text-stone-900 mb-8 text-center">Cat Litter Types Explained</h2>
        <div className="space-y-6">
          {litterTypes.map((litter, index) => (
            <Card key={index} className={`overflow-hidden ${litter.recommended === 'top' ? 'ring-2 ring-green-500' : litter.recommended === 'kitten' ? 'ring-2 ring-amber-400' : ''}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center text-3xl">
                      {litter.icon}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold text-stone-900">{litter.name}</h3>
                      {litter.recommended === 'top' && <Badge className="bg-green-600 text-white">Top Pick</Badge>}
                      {litter.recommended === 'kitten' && <Badge className="bg-amber-500 text-white">Best for Kittens</Badge>}
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < litter.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300'}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-stone-600 mb-4">{litter.description}</p>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <p className="font-semibold text-green-700 mb-2 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" /> Pros
                        </p>
                        <ul className="text-sm text-stone-600 space-y-1">
                          {litter.pros.map((pro, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-green-500 mt-1">•</span> {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-semibold text-red-700 mb-2 flex items-center gap-1">
                          <X className="w-4 h-4" /> Cons
                        </p>
                        <ul className="text-sm text-stone-600 space-y-1">
                          {litter.cons.map((con, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-red-500 mt-1">•</span> {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-semibold text-blue-700 mb-2 flex items-center gap-1">
                          <Cat className="w-4 h-4" /> Best For
                        </p>
                        <p className="text-sm text-stone-600">{litter.bestFor}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tips Section */}
        <div className="mt-12">
          <h2 className="text-3xl font-bold text-stone-900 mb-8 text-center">Litter Box Tips</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: "📏", title: "Depth Matters", tip: "Fill litter to 2-3 inches deep for optimal clumping and digging." },
              { icon: "🧹", title: "Scoop Daily", tip: "Remove waste at least once daily to keep the box fresh and inviting." },
              { icon: "🔄", title: "Full Change", tip: "Replace all litter every 2-4 weeks depending on type and usage." },
              { icon: "📍", title: "Location", tip: "Place the box in a quiet, accessible area away from food and water." },
              { icon: "➕", title: "Multiple Cats", tip: "Have one litter box per cat, plus one extra, in different locations." },
              { icon: "🚿", title: "Clean the Box", tip: "Wash the litter box with mild soap monthly - avoid harsh chemicals." }
            ].map((item, i) => (
              <Card key={i} className="bg-white">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <h4 className="font-semibold text-stone-900 mb-1">{item.title}</h4>
                      <p className="text-sm text-stone-600">{item.tip}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA */}
        <Card className="mt-12 bg-amber-600 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Need More Advice?</h2>
            <p className="text-amber-100 mb-6 max-w-2xl mx-auto">
              Our team is always happy to help with cat care questions. Get in touch if you need personalised litter recommendations!
            </p>
            <Button 
              onClick={() => window.location.href = '/contact'} 
              className="bg-white text-amber-600 hover:bg-amber-50 rounded-full px-8"
            >
              <Phone className="w-4 h-4 mr-2" /> Contact Us
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Brands We Love Page
export const BrandsPage = () => {
  const brands = [
    { name: "Lily's Kitchen", category: "Premium Pet Food", description: "Natural, nutritious recipes made with proper meat.", logo: "🐱" },
    { name: "Catit", category: "Cat Accessories", description: "Innovative products for curious cats.", logo: "🎮" },
    { name: "Feliway", category: "Cat Calming", description: "Pheromone diffusers for stress relief.", logo: "😌" },
    { name: "World's Best Cat Litter", category: "Litter", description: "Natural, flushable cat litter.", logo: "🌾" }
  ];

  return (
    <div className="min-h-screen pt-36 bg-stone-50" data-testid="brands-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <Badge className="bg-amber-100 text-amber-700 mb-4">Recommendations</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Brands We <span className="text-amber-600">Love</span></h1>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">Products we trust and recommend for your feline friends.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {brands.map((brand, i) => (
            <Card key={i} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <span className="text-4xl">{brand.logo}</span>
                  <div>
                    <Badge className="bg-stone-100 text-stone-600 mb-2">{brand.category}</Badge>
                    <h3 className="font-bold text-lg text-stone-900">{brand.name}</h3>
                    <p className="text-sm text-stone-600">{brand.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default { IndoorOutdoorPage, MicrochippingPage, ScanAngelsPage, BrandsPage, FoodGuidePage, LitterGuidePage };
